import React, { useCallback, useMemo, useRef, useState } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";

/**
 * Phase 133 — filters no longer auto-fetch. Every list / report page keeps its filter widgets live in
 * local state, but the API is only called when the user clicks **Fetch** (or changes a page / sort —
 * those still fetch immediately, using the LAST APPLIED filters).
 *
 *   const gate = useFetchGate(filters, { autoFetch: arrivedFiltered });
 *   const params = gate.applied;            // null until the first Fetch
 *   useEffect(() => { if (gate.fetched) load(gate.applied); }, [gate.applied, gate.version]);
 *
 * `applied`  — snapshot of the filters at the last Fetch (null before the first one)
 * `fetched`  — has the user fetched at least once?
 * `dirty`    — do the live filters differ from the applied snapshot? (the button highlights)
 * `version`  — bumps on every Fetch so a click with unchanged filters still re-runs the query
 * `fetch()`  — apply the live filters; `reset()` — back to the "nothing fetched" state
 *
 * `autoFetch: true` fetches once on mount — used when the visitor ARRIVED with filters in the URL
 * (a dashboard drilldown / shared link is itself the user's request for that data).
 */
export function useFetchGate(filters, { autoFetch = false } = {}) {
  const [applied, setApplied] = useState(() => (autoFetch ? filters : null));
  const [version, setVersion] = useState(autoFetch ? 1 : 0);
  const live = useRef(filters);
  live.current = filters;
  const fetch = useCallback(() => {
    setApplied(live.current);
    setVersion((v) => v + 1);
  }, []);
  const reset = useCallback(() => { setApplied(null); }, []);
  const liveKey = JSON.stringify(filters);
  const appliedKey = useMemo(() => (applied == null ? null : JSON.stringify(applied)), [applied]);
  const dirty = appliedKey != null && appliedKey !== liveKey;
  return { applied, appliedKey, fetched: applied != null, dirty, version, fetch, reset };
}

/** The Fetch button. Pulses (default variant) until the first fetch and whenever the filters changed since. */
export function FetchButton({ onClick, loading = false, dirty = false, fetched = false, label = "Fetch",
  className = "", size = "default", testId = "fetch-button", disabled = false, type = "button" }) {
  const emphasised = !fetched || dirty;
  return (
    <Button type={type} size={size} variant={emphasised ? "default" : "outline"} onClick={onClick}
      disabled={disabled || loading} className={cn("h-10 min-w-[104px]", className)}
      data-testid={testId} data-dirty={dirty ? "true" : "false"} data-fetched={fetched ? "true" : "false"}
      title={dirty ? "Filters changed — click to load the results" : fetched ? "Reload with the current filters" : "Load results for the selected filters"}>
      <Search className={cn("h-4 w-4 mr-1.5", loading && "animate-pulse")} aria-hidden="true" />
      {loading ? "Fetching…" : label}
    </Button>
  );
}

/** Small "filters changed" hint shown beside the button after the first fetch. */
export function FetchDirtyHint({ dirty, testId = "fetch-dirty-hint", className = "" }) {
  if (!dirty) return null;
  return (
    <span className={cn("text-xs text-[hsl(var(--warning))] font-medium", className)} role="status" data-testid={testId}>
      Filters changed — click Fetch to update
    </span>
  );
}

/** Empty state shown in place of the results until the first Fetch. */
export function FetchPrompt({ title = "Set your filters and click Fetch", subtitle = "Nothing is loaded until you ask for it — choose the filters above, then click Fetch to load the results.",
  onFetch, testId = "fetch-prompt", compact = false, className = "" }) {
  return (
    <div className={cn("flex flex-col items-center justify-center text-center", compact ? "py-8" : "py-14", className)}
      role="status" data-testid={testId}>
      <div className="h-12 w-12 rounded-full bg-accent flex items-center justify-center mb-3">
        <SlidersHorizontal className="h-6 w-6 text-accent-foreground" aria-hidden="true" />
      </div>
      <p className="font-medium">{title}</p>
      {subtitle && <p className="text-sm text-muted-foreground mt-1 max-w-md">{subtitle}</p>}
      {onFetch && (
        <Button className="mt-4 h-10" onClick={onFetch} data-testid={`${testId}-button`}>
          <Search className="h-4 w-4 mr-1.5" aria-hidden="true" /> Fetch
        </Button>
      )}
    </div>
  );
}

/** Wrap a filter bar so pressing Enter in any text / date input triggers Fetch. */
export function FetchForm({ onFetch, children, className = "", testId }) {
  return (
    <form className={className} data-testid={testId}
      onSubmit={(e) => { e.preventDefault(); onFetch?.(); }}>
      {children}
    </form>
  );
}

/** Territory helper shared by every staff dropdown that follows Zone / Ward (Phase 117 semantics):
 *  ward is the finer filter, so when both are set the ward decides. */
export const staffInTerritory = (u, zoneId, wardId) => {
  // Phase 176: zoneId / wardId may be comma-joined lists — any selected id matches
  const ids = (s) => String(s || "").split(",").map((x) => x.trim()).filter(Boolean);
  const w = ids(wardId), z = ids(zoneId);
  if (w.length) return (u.ward_ids || []).some((id) => w.includes(id));
  if (z.length) return (u.zone_ids || []).some((id) => z.includes(id));
  return true;
};
